import { useNavigate } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip } from 'recharts'
import {
  SectionCard, SectionAction, StatTile, StatusBadge, EmptyState, Skeleton, useThemeColors,
} from '@lighthouse/design'
import { IconAlertCircle } from '@tabler/icons-react'
import { useThroughput } from '../../hooks/useThroughput'
import { useTopEntities } from '../../hooks/useTopEntities'
import { useWorkerFleet } from '../../hooks/useOverview'
import {
  NOT_MEASURED, agoLabel, compactNumber, duration, exactNumber, relativeTime, shortDuration, whenLabel,
} from '../../utils/format'
import type { ThroughputHour } from '../../types/throughput'
import type { WorkerNode } from '../../types/observatory'
import {
  NO_RUNS_TEXT, THROUGHPUT_HOURS, TOP_ENTITIES_SUBTITLE, TOP_ENTITY_COUNT, TOP_ENTITY_DAYS,
  fleetUtilizationTile, latestHourText, queryView, requestErrorText, throughputTiles,
} from './overviewFormat'

/* ---- Throughput: SMALL MULTIPLES, never dual-axis ------------------------ */

interface PanelSpec {
  key: keyof Pick<ThroughputHour, 'recordsProcessed' | 'failureRatePercent' | 'avgDurationSeconds'>
  label: string
  color: string
  format: (v: number) => string
}

function MiniPanel({ spec, points, showAxis, grid, axis, muted }: {
  spec: PanelSpec
  points: ThroughputHour[]
  showAxis: boolean
  grid: string
  axis: string
  muted: string
}) {
  return (
    <div data-panel={spec.key}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2, gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--color-text-secondary)' }}>{spec.label}</span>
        {/* The last bucket is the hour in progress, so it is labelled as partial —
            otherwise every chart reads as a drop in the current hour. A null
            (nothing finished that hour) prints the dash, never 0. */}
        <span style={{ fontSize: 11, color: 'var(--color-text-disabled)', fontVariantNumeric: 'tabular-nums' }}>
          {latestHourText(points[points.length - 1]?.[spec.key], spec.format)}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={showAxis ? 96 : 78}>
        <LineChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={grid} vertical={false} />
          <XAxis
            dataKey="hourUtc"
            hide={!showAxis}
            tick={{ fontSize: 10, fill: muted }}
            stroke={axis}
            tickFormatter={(v: string) => new Date(v).toLocaleTimeString(undefined, { hour: 'numeric' })}
            interval="preserveStartEnd"
            minTickGap={40}
          />
          <YAxis
            width={46}
            tick={{ fontSize: 10, fill: muted }}
            stroke={axis}
            tickFormatter={spec.format}
          />
          <RTooltip
            contentStyle={{
              background: 'var(--color-bg-card)', border: '1px solid var(--color-border)',
              borderRadius: 4, fontSize: 11,
            }}
            labelFormatter={(v) => `${new Date(v as string).toLocaleString()} — UTC hour ${String(v).slice(11, 16)}`}
            formatter={(v) => [v == null ? NOT_MEASURED : spec.format(v as number), spec.label]}
          />
          {/* No connectNulls: an hour in which nothing finished has no rate and
              no duration, and it plots as a GAP — never as a zero. */}
          <Line
            type="monotone"
            dataKey={spec.key}
            stroke={spec.color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

/**
 * ETL Throughput and Reliability, from `api/observatory/throughput` — finished
 * entity runs in hourly UTC buckets.
 *
 * Volume, failure rate and duration are three different scales. Putting them on
 * one chart would require two y-axes, which is the single worst chart mistake
 * there is — so these are SMALL MULTIPLES: three stacked panels sharing one
 * x-axis, each with its own scale. Only the bottom panel carries the axis labels,
 * since the domain is shared.
 *
 * Nothing is coerced: a null rate is a gap, a null tile is a dash, a delta
 * exists only when the server served a prior window, and a window in which
 * nothing finished says so in words rather than drawing three flat zero lines.
 * The served remarks — what the figures mean, and how a resume bends them — sit
 * under the tiles, because they are what stops a tile overclaiming.
 */
export function ThroughputPanels() {
  const q = useThroughput(THROUGHPUT_HOURS)
  const state = queryView(q)
  const t = useThemeColors()

  const specs: PanelSpec[] = [
    { key: 'recordsProcessed', label: 'Records processed', color: t.chartSlot1, format: (v) => compactNumber(v) },
    { key: 'failureRatePercent', label: 'Failure rate (runs)', color: t.statusCritical, format: (v) => `${v.toFixed(1)}%` },
    { key: 'avgDurationSeconds', label: 'Avg duration', color: t.chartSlot2, format: (v) => duration(v) },
  ]

  return (
    <SectionCard
      title="ETL Throughput and Reliability"
      subtitle={`Last ${THROUGHPUT_HOURS} hours · hourly buckets aligned to UTC`}
    >
      {state === 'error' ? (
        <EmptyState
          icon={<IconAlertCircle size={28} stroke={1.5} />}
          title="Could not load throughput"
          message={requestErrorText('ETL throughput', q.error)}
        />
      ) : state === 'loading' || !q.data ? (
        <Skeleton height={300} />
      ) : q.data.totals.runCount === 0 ? (
        <EmptyState
          title={NO_RUNS_TEXT}
          message={q.data.telemetryReachesBackTo
            ? `The earliest finished entity run on record is ${whenLabel(q.data.telemetryReachesBackTo)}.`
            : 'No entity run has ever finished against this database.'}
        />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {specs.map((spec, i) => (
              <MiniPanel
                key={spec.key}
                spec={spec}
                points={q.data!.points}
                showAxis={i === specs.length - 1}
                grid={t.chartGrid}
                axis={t.chartAxis}
                muted={t.chartNeutral}
              />
            ))}
          </div>

          <div
            className="lh-overview-grid"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', marginTop: 14 }}
          >
            {throughputTiles(q.data).map((tile) => (tile.delta ? (
              <StatTile
                key={tile.key}
                label={tile.label}
                value={tile.value}
                size="sm"
                delta={tile.delta.change}
                deltaPolarity={tile.delta.polarity}
                deltaLabel={tile.delta.label}
                sublabel={tile.sublabel ?? undefined}
              />
            ) : (
              <StatTile key={tile.key} label={tile.label} value={tile.value} size="sm" sublabel={tile.sublabel ?? undefined} />
            )))}
          </div>

          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--color-text-secondary)' }} data-remarks="">
            {q.data.previous == null && q.data.previousUnavailableReason && (
              <p style={{ margin: '0 0 4px' }}>No prior window: {q.data.previousUnavailableReason}</p>
            )}
            {q.data.remarks.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {q.data.remarks.map((remark) => <li key={remark}>{remark}</li>)}
              </ul>
            )}
          </div>
        </>
      )}
    </SectionCard>
  )
}

/* ---- Top entities: nominal categorical, one hue, direct labels ----------- */

/**
 * Entities by records processed in SUCCESSFUL loads — `api/metrics/entities`
 * counts `EtlCompleted` activity rows only, so a failed run contributes nothing
 * and the subtitle says so. The window the subtitle names is the one the request
 * sends: both read TOP_ENTITY_DAYS.
 */
export function TopEntitiesChart() {
  const navigate = useNavigate()
  const q = useTopEntities(TOP_ENTITY_COUNT, TOP_ENTITY_DAYS)
  const state = queryView(q)
  const rows = state === 'ready' ? q.data! : []
  const max = Math.max(1, ...rows.map((d) => d.totalRecords))

  return (
    <SectionCard
      title="Top ETL Entities by Volume"
      subtitle={TOP_ENTITIES_SUBTITLE}
      flush
      action={<SectionAction onClick={() => navigate('/etl-explorer')}>View all →</SectionAction>}
    >
      {state === 'error' ? (
        <EmptyState
          icon={<IconAlertCircle size={28} stroke={1.5} />}
          title="Could not load entity volumes"
          message={requestErrorText('entity volumes', q.error)}
        />
      ) : state === 'loading' ? (
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.from({ length: TOP_ENTITY_COUNT }, (_, i) => <Skeleton key={i} height={26} />)}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No successful loads in this window"
          message="Every ETL job is manual-trigger; nothing completed in the window above."
        />
      ) : (
        <div style={{ paddingBottom: 8 }}>
          {rows.map((row, i) => (
            <div
              className="lh-bar-row"
              key={row.entityName}
              title={`${exactNumber(row.runCount)} successful load${row.runCount === 1 ? '' : 's'}, the last ${agoLabel(row.lastRun)}`}
            >
              <span className="lh-bar-rank">{i + 1}</span>
              <span style={{ minWidth: 0 }}>
                <span className="lh-bar-label">{row.entityName}</span>
                <span className="lh-bar-track">
                  <span className="lh-bar-fill" style={{ width: `${(row.totalRecords / max) * 100}%` }} />
                </span>
              </span>
              {/* Direct value label — this is the relief channel, so the bars need
                  no legend and no axis. */}
              <span className="lh-bar-value">{compactNumber(row.totalRecords)}</span>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  )
}

/* ---- Worker Utilization: real heartbeats, per server --------------------- */

/**
 * One server's row.
 *
 * A live server gets a utilization bar; a dead one gets a status badge instead.
 * That asymmetry is the point — a bar at 0% reads as "idle and available", which
 * is the opposite of what a silent worker means, and it is the reading an
 * operator would act on wrongly.
 */
function WorkerRow({ worker }: { worker: WorkerNode }) {
  return (
    <div className="lh-bar-row" key={worker.workerId}>
      <span className="lh-bar-rank" />
      <span style={{ minWidth: 0 }}>
        <span className="lh-bar-label" title={worker.workerId}>
          {worker.name}
          {worker.queues.length > 0 && (
            <span style={{ color: 'var(--color-text-disabled)', fontSize: 11, marginLeft: 6 }}>
              {worker.queues.join(' · ')}
            </span>
          )}
        </span>
        {worker.isLive ? (
          <span className="lh-bar-track">
            <span className="lh-bar-fill" style={{ width: `${worker.utilizationPercent}%` }} />
          </span>
        ) : (
          <span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-disabled)' }}>
            Last heartbeat {worker.lastHeartbeatAt ? relativeTime(worker.lastHeartbeatAt) : 'never'}
          </span>
        )}
      </span>
      {worker.isLive ? (
        <span className="lh-bar-value" title={`${worker.busyWorkerCount} of ${worker.workerCount} slots busy`}>
          {worker.utilizationPercent}%
        </span>
      ) : (
        <StatusBadge status={worker.status} label={worker.statusLabel} />
      )}
    </div>
  )
}

/**
 * Worker Utilization.
 *
 * Every figure here is measured. It previously drew five invented workers, and was
 * then deleted on the premise that no worker identity or heartbeat existed
 * anywhere in the backend — which was false. `HangFire.Server` has always carried
 * the server id, a refreshed heartbeat, the configured slot count and the queue
 * list; the API simply could not read them, because it holds no Hangfire
 * reference (D14). Automaton projects them into `Etl.WorkerHeartbeat` and the API
 * serves that.
 *
 * The panel distinguishes three states a single "no data" would flatten:
 * nothing has ever reported, the projection itself is stale (Automaton is down,
 * so every number is history), and a server has stopped beating while the
 * projection is healthy (that server is gone).
 */
export function WorkerUtilization() {
  const { data, isLoading, isError } = useWorkerFleet()
  const navigate = useNavigate()

  return (
    <SectionCard
      title="Worker Utilization"
      flush
      action={
        // `SectionAction` also takes an `href`, but that renders a bare <a> and a
        // bare <a> inside BrowserRouter is a full page reload. Every other in-app
        // navigation in Observatory goes through useNavigate, and this panel is
        // rendered inside the router, so it does too. The /workers page shares this
        // panel's query key, so arriving there costs no extra fetch.
        <SectionAction onClick={() => navigate('/workers')}>
          View workers →
        </SectionAction>
      }
    >
      {isError ? (
        <EmptyState title="Could not load workers" message="The worker projection did not respond." />
      ) : isLoading || !data ? (
        <div style={{ padding: 12 }}>{Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={24} />)}</div>
      ) : data.hasNeverReported ? (
        <EmptyState
          title="No worker has reported yet"
          message="Automaton publishes a heartbeat every 15 seconds once it is running. Nothing has been recorded against this database."
        />
      ) : (
        <>
          <div style={{ padding: '10px 16px 4px' }}>
            {/* A rate only where there is capacity to measure: fleetUtilizationTile dashes a
                stale projection and a fleet with no live worker, which would otherwise read "0%". */}
            <StatTile label="Fleet utilization" size="sm" {...fleetUtilizationTile(data)} />
          </div>

          {/*
            The projection being stale is a different failure from a worker being
            stale, and it invalidates EVERY number above — including the healthy
            ones. Said plainly rather than left for someone to infer from a
            timestamp.
          */}
          {data.isProjectionStale && (
            <div style={{ padding: '0 16px 8px', fontSize: 11, color: 'var(--status-warning-fg)' }}>
              These figures are stale — Automaton last published{' '}
              {data.observedAt ? relativeTime(data.observedAt) : 'never'}. The worker host may be down.
            </div>
          )}

          {data.unresponsiveWorkerCount > 0 && !data.isProjectionStale && (
            <div style={{ padding: '0 16px 8px', fontSize: 11, color: 'var(--status-critical-fg)' }}>
              {data.unresponsiveWorkerCount} worker
              {data.unresponsiveWorkerCount === 1 ? ' has' : 's have'} stopped responding.
            </div>
          )}

          <div style={{ paddingBottom: 8 }}>
            {data.workers.map((w) => <WorkerRow key={w.workerId} worker={w} />)}
          </div>

          {data.workers.some((w) => w.isLive && w.uptimeSeconds !== null) && (
            <div style={{ padding: '0 16px 12px', fontSize: 11, color: 'var(--color-text-disabled)' }}>
              Longest uptime{' '}
              {shortDuration(Math.max(...data.workers.filter((w) => w.isLive).map((w) => w.uptimeSeconds ?? 0)))}
            </div>
          )}
        </>
      )}
    </SectionCard>
  )
}
